import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Drawer,
  Toolbar,
  IconButton,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PlayerSidebar from '@components/layout/PlayerSidebar';
import { getContestSports } from '@utils/sports';
import { getPortalPaths, type PortalMode } from '@utils/portalPaths';
import { getSportIcon } from '@utils/sportPresentation';
import { brandColors, brandGradients } from '@config/brandTokens';

const DRAWER_WIDTH = 280;

interface ContestOverviewPageProps {
  portalMode?: PortalMode;
}

const ContestOverviewPage: React.FC<ContestOverviewPageProps> = ({ portalMode = 'player' }) => {
  const navigate = useNavigate();
  const paths = getPortalPaths(portalMode);
  const hideSidebar = portalMode === 'coach';
  const [mobileOpen, setMobileOpen] = useState(false);
  const contestSports = getContestSports();

  const handleSportClick = (sport: string) => {
    navigate(paths.contestSport(sport, 'all'));
  };

  const mainContent = (
    <Container maxWidth="lg" sx={{ mb: 4, pt: hideSidebar ? 2 : 1.5 }}>
      <Typography
        variant="h6"
        sx={{ mb: 1.5, fontWeight: 900, color: brandColors.navy, letterSpacing: 0.2 }}
      >
        Contests
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.25,
        }}
      >
        {contestSports.map((sport) => (
          <Box
            key={sport}
            component="button"
            type="button"
            onClick={() => handleSportClick(sport)}
            aria-label={`Open ${sport} contests`}
            sx={{
              appearance: 'none',
              WebkitAppearance: 'none',
              font: 'inherit',
              border: `1px solid ${brandColors.blue}`,
              background: brandGradients.contestHeader,
              color: brandColors.white,
              borderRadius: 2,
              p: 1.1,
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.65,
              width: { xs: 'calc(50% - 6px)', sm: 128, md: 136 },
              minHeight: 86,
              boxShadow: '0 8px 18px rgba(17,23,57,0.18)',
              transition: 'transform 0.15s ease, filter 0.15s ease',
              '&:hover': {
                filter: 'brightness(1.08)',
                transform: 'translateY(-2px)',
              },
              '&:focus-visible': {
                outline: `2px solid ${brandColors.blue}`,
                outlineOffset: 2,
              },
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                bgcolor: 'rgba(255,255,255,0.16)',
                color: brandColors.white,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {getSportIcon(sport, 22)}
            </Box>
            <Typography
              component="span"
              sx={{
                fontWeight: 800,
                fontSize: '0.82rem',
                lineHeight: 1.15,
                textAlign: 'center',
                color: brandColors.white,
              }}
            >
              {sport}
            </Typography>
          </Box>
        ))}
      </Box>
    </Container>
  );

  if (hideSidebar) {
    return mainContent;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: brandColors.ice }}>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}
      >
        <PlayerSidebar />
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{ display: { xs: 'none', md: 'block' }, width: DRAWER_WIDTH, '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}
        open
      >
        <PlayerSidebar />
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, width: { md: `calc(100% - ${DRAWER_WIDTH}px)` } }}>
        <Toolbar sx={{ display: { md: 'none' } }}>
          <IconButton onClick={() => setMobileOpen(true)}>
            <MenuIcon />
          </IconButton>
        </Toolbar>
        {mainContent}
      </Box>
    </Box>
  );
};

export default ContestOverviewPage;
